/**
 * Service for logging shift scheduling activities
 * Provides methods to log various scheduling events
 */

import { supabase } from '@/integrations/supabase/client';
import type {
  ShiftSchedulingEventType,
  ShiftSchedulingActivityMetadata,
  ActorType,
} from '@/types/shift-scheduling-activity';
import { format } from 'date-fns';

interface LogActivityParams {
  businessId: string;
  shiftId?: string | null;
  eventType: ShiftSchedulingEventType;
  actorType: ActorType;
  actorUserId?: string | null;
  actorEmployeeId?: string | null;
  targetEmployeeId?: string | null;
  shiftTitle?: string | null;
  shiftDate: Date;
  shiftStartTime?: Date | null;
  shiftEndTime?: Date | null;
  metadata?: ShiftSchedulingActivityMetadata;
  description?: string;
}

export class ShiftActivityLogger {
  /**
   * Log a shift scheduling activity event
   */
  static async logActivity(params: LogActivityParams): Promise<string | null> {
    try {
      const { data, error } = await supabase
        .from('shift_scheduling_activity_log')
        .insert({
          business_id: params.businessId,
          shift_id: params.shiftId,
          event_type: params.eventType,
          actor_type: params.actorType,
          actor_user_id: params.actorUserId,
          actor_employee_id: params.actorEmployeeId,
          target_employee_id: params.targetEmployeeId,
          shift_title: params.shiftTitle,
          shift_date: format(params.shiftDate, 'yyyy-MM-dd'),
          shift_start_time: params.shiftStartTime?.toISOString(),
          shift_end_time: params.shiftEndTime?.toISOString(),
          metadata: params.metadata || {},
          description: params.description,
        })
        .select('id')
        .single();

      if (error) throw error;
      return data?.id || null;
    } catch (error) {
      console.error('Failed to log shift activity:', error);
      return null;
    }
  }

  /**
   * Get current user ID for logging
   */
  static async getCurrentUserId(): Promise<string | null> {
    const { data } = await supabase.auth.getUser();
    return data?.user?.id || null;
  }

  /**
   * Log shift creation event
   */
  static async logShiftCreated(
    businessId: string,
    shift: {
      id: string;
      title?: string | null;
      startTime: Date;
      endTime: Date;
      employeeId?: string | null;
    },
    actorUserId: string,
    source: ShiftSchedulingActivityMetadata['source'] = 'calendar'
  ): Promise<string | null> {
    return this.logActivity({
      businessId,
      shiftId: shift.id,
      eventType: 'shift_created',
      actorType: 'manager',
      actorUserId,
      targetEmployeeId: shift.employeeId,
      shiftTitle: shift.title,
      shiftDate: shift.startTime,
      shiftStartTime: shift.startTime,
      shiftEndTime: shift.endTime,
      metadata: { source },
      description: `Created shift "${shift.title || 'Untitled'}"`,
    });
  }

  /**
   * Log shift edited event
   */
  static async logShiftEdited(
    businessId: string,
    shiftId: string,
    shiftDate: Date,
    shiftTitle: string | null,
    changes: ShiftSchedulingActivityMetadata['changes'],
    actorUserId: string
  ): Promise<string | null> {
    const changeDescriptions: string[] = [];
    if (changes?.title) changeDescriptions.push('title');
    if (changes?.notes) changeDescriptions.push('notes');
    if (changes?.breaks) changeDescriptions.push('breaks');

    return this.logActivity({
      businessId,
      shiftId,
      eventType: 'shift_edited',
      actorType: 'manager',
      actorUserId,
      shiftTitle,
      shiftDate,
      metadata: { changes },
      description: `Edited shift: ${changeDescriptions.join(', ')}`,
    });
  }

  /**
   * Log shift moved event (time/date changed via drag-and-drop or edit)
   */
  static async logShiftMoved(
    businessId: string,
    shiftId: string,
    shiftTitle: string | null,
    previousStart: Date,
    previousEnd: Date,
    newStart: Date,
    newEnd: Date,
    actorUserId: string
  ): Promise<string | null> {
    const sameDayMove = format(previousStart, 'yyyy-MM-dd') === format(newStart, 'yyyy-MM-dd');
    const description = sameDayMove
      ? `Moved shift from ${format(previousStart, 'HH:mm')} to ${format(newStart, 'HH:mm')}`
      : `Moved shift from ${format(previousStart, 'MMM d, HH:mm')} to ${format(newStart, 'MMM d, HH:mm')}`;

    return this.logActivity({
      businessId,
      shiftId,
      eventType: 'shift_moved',
      actorType: 'manager',
      actorUserId,
      shiftTitle,
      shiftDate: newStart,
      shiftStartTime: newStart,
      shiftEndTime: newEnd,
      metadata: {
        previousStart: previousStart.toISOString(),
        previousEnd: previousEnd.toISOString(),
        newStart: newStart.toISOString(),
        newEnd: newEnd.toISOString(),
        previousDate: format(previousStart, 'yyyy-MM-dd'),
        newDate: format(newStart, 'yyyy-MM-dd'),
      },
      description,
    });
  }

  /**
   * Log shift deletion event
   */
  static async logShiftDeleted(
    businessId: string,
    shiftId: string,
    shiftTitle: string | null,
    shiftDate: Date,
    shiftStartTime: Date | null,
    shiftEndTime: Date | null,
    targetEmployeeId: string | null,
    actorUserId: string
  ): Promise<string | null> {
    return this.logActivity({
      businessId,
      shiftId,
      eventType: 'shift_deleted',
      actorType: 'manager',
      actorUserId,
      targetEmployeeId,
      shiftTitle,
      shiftDate,
      shiftStartTime,
      shiftEndTime,
      description: `Deleted shift "${shiftTitle || 'Untitled'}"`,
    });
  }

  /**
   * Log employee assignment event
   */
  static async logEmployeeAssigned(
    businessId: string,
    shiftId: string,
    shiftDate: Date,
    shiftTitle: string | null,
    newEmployeeId: string,
    newEmployeeName: string | null,
    previousEmployeeId: string | null,
    previousEmployeeName: string | null,
    actorUserId: string,
    actorType: ActorType = 'manager',
    actorEmployeeId?: string | null
  ): Promise<string | null> {
    const description = previousEmployeeId
      ? `Reassigned shift from ${previousEmployeeName || 'unknown'} to ${newEmployeeName || 'unknown'}`
      : `Assigned ${newEmployeeName || 'employee'} to shift`;

    return this.logActivity({
      businessId,
      shiftId,
      eventType: 'employee_assigned',
      actorType,
      actorUserId,
      actorEmployeeId,
      targetEmployeeId: newEmployeeId,
      shiftTitle,
      shiftDate,
      metadata: {
        previousEmployeeId,
        previousEmployeeName,
        newEmployeeName,
      },
      description,
    });
  }

  /**
   * Log employee unassignment event
   */
  static async logEmployeeUnassigned(
    businessId: string,
    shiftId: string,
    shiftDate: Date,
    shiftTitle: string | null,
    previousEmployeeId: string,
    previousEmployeeName: string | null,
    actorUserId: string
  ): Promise<string | null> {
    return this.logActivity({
      businessId,
      shiftId,
      eventType: 'employee_unassigned',
      actorType: 'manager',
      actorUserId,
      targetEmployeeId: previousEmployeeId,
      shiftTitle,
      shiftDate,
      metadata: {
        previousEmployeeId,
        previousEmployeeName,
      },
      description: `Unassigned ${previousEmployeeName || 'employee'} from shift`,
    });
  }

  /**
   * Log employee self-request event
   */
  static async logEmployeeSelfRequest(
    businessId: string,
    shiftId: string,
    shiftDate: Date,
    shiftTitle: string | null,
    employeeId: string,
    employeeName: string | null,
    requestId: string,
    employeeNotes?: string
  ): Promise<string | null> {
    return this.logActivity({
      businessId,
      shiftId,
      eventType: 'employee_self_requested',
      actorType: 'employee',
      actorEmployeeId: employeeId,
      targetEmployeeId: employeeId,
      shiftTitle,
      shiftDate,
      metadata: {
        requestId,
        employeeNotes,
        newEmployeeName: employeeName,
      },
      description: `${employeeName || 'Employee'} requested this shift`,
    });
  }

  /**
   * Log shift request approved event
   */
  static async logRequestApproved(
    businessId: string,
    shiftId: string,
    shiftDate: Date,
    shiftTitle: string | null,
    employeeId: string,
    employeeName: string | null,
    requestId: string,
    actorUserId: string,
    managerNotes?: string
  ): Promise<string | null> {
    return this.logActivity({
      businessId,
      shiftId,
      eventType: 'employee_request_approved',
      actorType: 'manager',
      actorUserId,
      targetEmployeeId: employeeId,
      shiftTitle,
      shiftDate,
      metadata: {
        requestId,
        managerNotes,
        newEmployeeName: employeeName,
      },
      description: `Approved ${employeeName || 'employee'}'s request for this shift`,
    });
  }

  /**
   * Log shift request rejected event
   */
  static async logRequestRejected(
    businessId: string,
    shiftId: string,
    shiftDate: Date,
    shiftTitle: string | null,
    employeeId: string,
    employeeName: string | null,
    requestId: string,
    actorUserId: string,
    managerNotes?: string
  ): Promise<string | null> {
    return this.logActivity({
      businessId,
      shiftId,
      eventType: 'employee_request_rejected',
      actorType: 'manager',
      actorUserId,
      targetEmployeeId: employeeId,
      shiftTitle,
      shiftDate,
      metadata: {
        requestId,
        managerNotes,
        newEmployeeName: employeeName,
      },
      description: `Rejected ${employeeName || 'employee'}'s request for this shift`,
    });
  }
}
